import { extendTheme } from 'native-base'

import type { Theme } from '../types'

/**
 * App-specific NativeBase configuration that has no home in the unified
 * tokens: component style overrides, font files, and legacy color groups
 * kept for backwards compatibility until Phase 3 migrates screens onto
 * `tokens` (e.g. CWA's `vermillion` scale with the non-standard 500 shade).
 */
export interface NativeBaseExtras {
  components?: Record<string, unknown>
  fontConfig?: Record<string, unknown>
  /** Extra top-level color groups merged alongside the token colors. */
  extraColors?: Record<string, unknown>
  /** Per-app font-role additions/overrides; typography unification is deferred (DECISIONS.md). */
  fonts?: Record<string, unknown>
  fontSizes?: Record<string, unknown>
  space?: Record<string, unknown>
  config?: Record<string, unknown>
}

/**
 * Adapts a resolved token theme to a NativeBase theme. This file is the only
 * place in `packages/ui` that knows NativeBase exists; when the primitive
 * layer changes (Decision D6, CBP-3942), a sibling adapter replaces this one
 * with no call-site churn.
 *
 * The return type is left inferred so the token color groups (`brand`, etc.)
 * stay statically known on `theme.colors` at the call sites. An app needing
 * NativeBase's `ICustomTheme` augmentation should still anchor it to a
 * type-only literal `extendTheme` call (see apps/web configureTheme.ts) —
 * app-specific `components` do not survive this signature.
 */
export function createNativeBaseTheme(theme: Theme, extras: NativeBaseExtras = {}) {
  const { components, fontConfig, extraColors, fonts, fontSizes, space, config } = extras
  return extendTheme({
    colors: { ...theme.colors, ...extraColors },
    fontSizes: { ...theme.fontSizes, ...fontSizes },
    fonts: { ...theme.fonts, ...fonts },
    space,
    fontConfig,
    components,
    config: { initialColorMode: 'light', ...config },
  })
}

export type NativeBaseTheme = ReturnType<typeof createNativeBaseTheme>
